import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { axiosPrivate } from "../../api/axios";
import { 
  Box, 
  Button, 
  Card, 
  CardHeader, 
  CardContent, 
  useTheme, 
  Tabs, 
  Tab,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import MasterAgGrid from '../common/DataGrid/MasterAgGrid';
import MasterDataDialog from './MasterDataDialog';
import BatchAddMachineDialog from './BatchAddMachineDialog';
import { useTranslation } from "react-i18next";

const MasterPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const gridRef = useRef();
  const theme = useTheme();
  const { group_code, project_code } = useParams();
  const [columnDefs, setColumnDefs] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [pageSize, setPageSize] = useState(100);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  
  // 탭 관련 상태
  const tabs = useMemo(() => [
    { id: 'machines', name: t('pages.master.mch.tabs.machine') },
    { id: 'machine-groups', name: t('pages.master.mch.tabs.machineGroup') },
    { id: 'machine-categories', name: t('pages.master.mch.tabs.machineCategory') },
  ], [t]);
  const [selectedTab, setSelectedTab] = useState('machines');

  // 프로젝트 이름 상태 추가
  const [projectName, setProjectName] = useState('');

  // 데이터 다이얼로그 상태 추가
  const [dataDialogOpen, setDataDialogOpen] = useState(false);
  const [dataDialogMode, setDataDialogMode] = useState('add'); // 'add' 또는 'edit'
  const [currentData, setCurrentData] = useState(null);
  const [machineGroupsData, setMachineGroupsData] = useState([]);
  const [machineCategoriesData, setMachineCategoriesData] = useState([]);
  const [usedDtSyncIds, setUsedDtSyncIds] = useState([]);
  const [batchAddDialogOpen, setBatchAddDialogOpen] = useState(false);

  // 장비 그룹/분류 목록 재조회 (다이얼로그 옵션 최신화용)
  const refreshMasterLists = useCallback(async () => {
    try {
      const [groupsResponse, categoriesResponse] = await Promise.all([
        axiosPrivate.post('/master/machine-groups', { group_code, project_code }),
        axiosPrivate.post('/master/machine-categories', { group_code, project_code })
      ]);
      setMachineGroupsData(groupsResponse.data);
      setMachineCategoriesData(categoriesResponse.data);
    } catch (err) {
      console.error("마스터 데이터 로드 실패:", err);
    }
  }, [group_code, project_code]);

  // 페이지 제목 설정
  useEffect(() => {
    document.title = t('pages.master.mch.title');
  }, [t]);

  // 데이터 조회 함수
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // 선택된 탭에 따라 다른 API 호출
      let endpoint = '';
      
      if (selectedTab === 'machines') {
        endpoint = '/master/machines';
      } else if (selectedTab === 'machine-groups') {
        endpoint = '/master/machine-groups';
      } else if (selectedTab === 'machine-categories') {
        endpoint = '/master/machine-categories';
      }
      
      const response = await axiosPrivate.post(endpoint, {
        group_code,
        project_code
      });

      // 데이터 처리
      if (selectedTab === 'machines') {
        // 장비 관리 열 설정
        setColumnDefs([
          { field: 'machine_id', headerName: t('pages.master.mch.columns.machineId'), editable: false },
          { field: 'dt_sync_id', headerName: t('pages.master.mch.columns.dtSyncId'), editable: false },
          { field: 'machine_name', headerName: t('pages.master.mch.columns.machineName'), editable: false },
          { field: 'machine_manage_code', headerName: t('pages.master.mch.columns.manageCode'), editable: false },
          { field: 'machine_category_name', headerName: t('pages.master.mch.columns.machineCategory'), editable: false, valueFormatter: p => p.value || '미분류' },
          { field: 'machine_group_name', headerName: t('pages.master.mch.columns.machineGroup'), editable: false, valueFormatter: p => p.value || '미분류' },
          { field: 'machine_manufacturer', headerName: t('pages.master.mch.columns.manufacturer'), editable: false },
          { field: 'machine_model_name', headerName: t('pages.master.mch.columns.modelName'), editable: false },
          { field: 'machine_purchase_date', headerName: t('pages.master.mch.columns.purchaseDate'), editable: false },
          { field: 'machine_size', headerName: t('pages.master.mch.columns.size'), editable: false },
          { field: 'machine_volt', headerName: t('pages.master.mch.columns.volt'), editable: false },
          { field: 'updateDate', headerName: t('pages.master.mch.columns.updateDate'), editable: false }
        ]);
      } else if (selectedTab === 'machine-groups') {
        // 장비 그룹 열 설정
        setColumnDefs([
          { field: 'machine_group_id', headerName: t('pages.master.mch.columns.groupId'), editable: false },
          { field: 'machine_group_name', headerName: t('pages.master.mch.columns.groupName'), editable: false }
        ]);
      } else if (selectedTab === 'machine-categories') {
        // 장비 분류 열 설정
        setColumnDefs([
          { field: 'machine_category_id', headerName: t('pages.master.mch.columns.categoryId'), editable: false },
          { field: 'machine_category_name', headerName: t('pages.master.mch.columns.categoryName'), editable: false }
        ]);
      }
      
      // 응답 데이터에 id 추가
      const dataWithId = response.data.map((row, index) => ({
        ...row,
        id: row.id || row.machine_id || row.worker_id || row.machine_group_id || row.machine_category_id || index + 1,
        // updateDate가 없으면 현재 시간 추가
        updateDate: row.updateDate || new Date().toISOString()
      }));
  
      setRowData(dataWithId);
    } catch (err) {
      console.error("데이터 조회 실패:", err);
      setError(t('pages.master.mch.messages.loadError'));
    } finally {
      setLoading(false);
    }
  }, [group_code, project_code, selectedTab, t]);

  // 데이터 로드
  useEffect(() => {
      fetchData();
  }, [fetchData, selectedTab]);

  // 장비 그룹 및 분류 데이터 로드
  useEffect(() => {
    refreshMasterLists();
  }, [refreshMasterLists]);

  // 데이터 조회 함수에서 유효한 dt_sync_id 목록 추출
  useEffect(() => {
    if (selectedTab === 'machines') {
      // 현재 편집 중인 장비의 ID를 제외한 모든 유효한 dt_sync_id 추출
      // rowData가 비어있어도 .filter.map은 빈 배열을 반환하므로 안전합니다.
      const usedIds = rowData
        .filter(row => 
          row.dt_sync_id && 
          row.dt_sync_id !== '' && 
          (currentData ? row.machine_id !== currentData.machine_id : true)
        )
        .map(row => row.dt_sync_id);
      
      setUsedDtSyncIds(usedIds);
    } else {
      // 'machines' 탭이 아닌 경우, 목록을 비워줍니다.
      setUsedDtSyncIds([]);
    }
  }, [rowData, selectedTab, currentData]);

  // 행 추가 핸들러
  const handleAddRow = async () => {
    await refreshMasterLists();
    setDataDialogMode('add');
    setCurrentData(null);
    setDataDialogOpen(true);
  };

  // 일괄 추가 다이얼로그 열기 핸들러
  const handleOpenBatchAddDialog = async () => {
    await refreshMasterLists();
    setBatchAddDialogOpen(true);
  };

  // 일괄 추가 다이얼로그 닫기 핸들러
  const handleCloseBatchAddDialog = () => {
    setBatchAddDialogOpen(false);
  };

  // 행 삭제 핸들러
  const handleDeleteRow = async () => {
    if (selectedRows.length === 0) {
      return;
    }
    
    if (window.confirm(t('pages.master.mch.messages.deleteConfirm', { count: selectedRows.length }))) {
      setLoading(true);
      
      try {
        let endpoint = '';
        
        if (selectedTab === 'machines') {
          endpoint = '/master/machines/delete';
        } else if (selectedTab === 'machine-groups') {
          endpoint = '/master/machine-groups/delete';
        } else if (selectedTab === 'machine-categories') {
          endpoint = '/master/machine-categories/delete';
        }
        
        // 선택된 행의 ID 추출
        let idField = '';
        if (selectedTab === 'machines') {
          idField = 'machine_id';
        } else if (selectedTab === 'machine-groups') {
          idField = 'machine_group_id';
        } else if (selectedTab === 'machine-categories') {
          idField = 'machine_category_id';
        }
        
        const ids = selectedRows.map(row => row[idField]);
        
        await axiosPrivate.post(endpoint, {
          group_code,
          project_code,
          ids
        });
        
        // 데이터 다시 로드
        await fetchData();
        
        // 선택 초기화
        setSelectedRows([]);
        
        // 성공 메시지
        alert(t('pages.master.mch.messages.deleteSuccess', { count: ids.length }));
      } catch (err) {
        console.error("데이터 삭제 실패:", err);
        setError(t('pages.master.mch.messages.deleteError'));
      } finally {
        setLoading(false);
      }
    }
  };

  // 선택 변경 핸들러
  const onSelectionChanged = () => {
    if (gridRef.current && gridRef.current.api) {
      const selectedNodes = gridRef.current.api.getSelectedNodes();
      const selectedData = selectedNodes.map(node => node.data);
      setSelectedRows(selectedData);
    }
  };

  // 프로젝트 목록으로 이동
  const handleBackToProjects = () => {
    navigate('/projects');
  };

  // 탭 변경 핸들러
  const handleTabChange = async (event, newValue) => {
    setSelectedTab(newValue);
    if (newValue === 'machines') {
      await refreshMasterLists();
    }
    
    // 탭 변경 후 그리드 크기 조정
    setTimeout(() => {
      if (gridRef.current && gridRef.current.api) {
        gridRef.current.api.sizeColumnsToFit();
        gridRef.current.api.autoSizeAllColumns();
      }
    }, 200);
  };

  // 타이틀 가져오기
  const getPageTitle = () => {
    const tab = tabs.find(t => t.id === selectedTab);
    const tabName = tab ? tab.name : t('pages.master.mch.title');
    
    if (projectName) {
      return `${tabName} (${projectName})`;
    }
    
    return tabName;
  };

  // 행 더블 클릭 핸들러 추가
  const handleRowDoubleClicked = (params) => {
    setDataDialogMode('edit');
    setCurrentData(params.data);
    setDataDialogOpen(true);
  };

  // 다이얼로그 저장 핸들러
  const handleSaveData = async (data, mode) => {
    setLoading(true);
    
    try {
      let endpoint = '';
      
      if (mode === 'add') {
        if (selectedTab === 'machines') {
          endpoint = '/master/machines/add';
        } else if (selectedTab === 'machine-groups') {
          endpoint = '/master/machine-groups/add';
        } else if (selectedTab === 'machine-categories') {
          endpoint = '/master/machine-categories/add';
        }
      } else if (mode === 'edit') {
      if (selectedTab === 'machines') {
          endpoint = '/master/machines/update';
          // machine_id가 있는지 확인 (안전을 위해)
          if (!data.machine_id) {
            throw new Error(t('pages.master.mch.messages.noMachineId'));
          }
      } else if (selectedTab === 'machine-groups') {
          endpoint = '/master/machine-groups/update';
      } else if (selectedTab === 'machine-categories') {
          endpoint = '/master/machine-categories/update';
        }
      } else if (mode === 'batch-add') {
        endpoint = '/master/machines/batch-add';
      }
      
      await axiosPrivate.post(endpoint, {
        group_code,
        project_code,
        data
      });
      
      // 데이터 다시 로드
      await fetchData();
      // 장비 그룹/분류 옵션 즉시 최신화
      await refreshMasterLists();
      
      // 다이얼로그 닫기
      setDataDialogOpen(false);
      setBatchAddDialogOpen(false);
      
      // 성공 메시지
      let successMessage = '';
      if (mode === 'add') {
        successMessage = t('pages.master.mch.messages.addSuccess');
      } else if (mode === 'edit') {
        successMessage = t('pages.master.mch.messages.editSuccess');
      } else if (mode === 'batch-add') {
        successMessage = t('pages.master.mch.messages.batchAddSuccess', { count: data.length });
      }
      alert(successMessage);
      
      // 그리드 새로고침 및 컬럼 크기 자동 조정
      setTimeout(() => {
        if (gridRef.current && gridRef.current.api) {
          gridRef.current.api.sizeColumnsToFit();
          gridRef.current.api.autoSizeAllColumns();
        }
      }, 100);
    } catch (err) {
      console.error("데이터 저장 실패:", err);
      alert(t('pages.master.mch.messages.saveError'));
    } finally {
      setLoading(false);
    }
  };

  // 다이얼로그 닫기 핸들러
  const handleCloseDialog = () => {
    setDataDialogOpen(false);
  };

  return (
    <Grid container spacing={3} sx={{ height: "100%" }}>
      <Grid size={{ xs: 12 }} sx={{ height: "100%"}}>
        <Card sx={{
          height: "100%",
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '0',
          bgcolor: theme.palette.mode === 'dark' 
            ? 'var(--mui-palette-background-default)' 
            : 'var(--mui-palette-background-paper)'
        }}>
          <CardHeader
            title={getPageTitle()}
            sx={{
              '& .MuiCardHeader-title': {
                fontSize: '22px',
                fontWeight: 600
              },
              paddingBlock: '16px'
            }}
            action={
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToProjects}
              >
                {t('pages.master.mch.buttons.projectList')}
              </Button>
            }
          />
          
          {/* 탭 네비게이션 */}
          <Box sx={{ 
            px: 6
          }}>
            <Tabs 
              value={selectedTab} 
              onChange={handleTabChange} 
              variant="scrollable"
              scrollButtons="auto"
              sx={{ 
                '& .MuiTabs-indicator': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minWidth: 'auto',
                  padding: '12px 16px',
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  '&.Mui-selected': {
                    color: theme.palette.primary.main,
                  },
                }
              }}
            >
              {tabs.map((tab) => (
                <Tab 
                  key={tab.id} 
                  value={tab.id} 
                  label={tab.name}
                  sx={{ 
                    minHeight: '48px',
                    '&.MuiButtonBase-root:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                />
              ))}
            </Tabs>
          </Box>
          
          {/* 에러 메시지 */}
          {error && (
            <Box sx={{ px: 6, pt: 2 }}>
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            </Box>
          )}
          
          {/* 버튼 그룹 - 오른쪽 정렬 */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            gap: 2,
            px: 6,
            pt: 2
          }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddRow}
              disabled={loading}
            >
              {t('pages.master.mch.buttons.add')}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteRow}
              disabled={selectedRows.length === 0 || loading}
            >
              {t('pages.master.mch.buttons.delete')}
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={<PlaylistAddIcon />}
              onClick={handleOpenBatchAddDialog}
              disabled={loading || selectedTab !== 'machines'}
            >
              {t('pages.master.mch.buttons.batchAdd')}
            </Button>
          </Box>

          <CardContent sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            flexDirection: 'column',
            paddingInline: 6,
            paddingBlock: 2,
            height: 0
          }}>
            <MasterAgGrid
              gridRef={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              onSelectionChanged={onSelectionChanged}
              pageSize={pageSize}
              loading={loading}
              onRowDoubleClicked={handleRowDoubleClicked}
            />
          </CardContent>
        </Card>
      </Grid>
      
      {/* 데이터 다이얼로그 */}
      <MasterDataDialog
        open={dataDialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveData}
        data={currentData}
        mode={dataDialogMode}
        type={selectedTab}
        machineGroups={machineGroupsData}
        machineCategories={machineCategoriesData}
        group_code={group_code}
        project_code={project_code}
        usedDtSyncIds={usedDtSyncIds}
      />
      
      <BatchAddMachineDialog
        open={batchAddDialogOpen}
        onClose={handleCloseBatchAddDialog}
        onSave={handleSaveData}
        machineGroups={machineGroupsData}
        machineCategories={machineCategoriesData}
        group_code={group_code}
        project_code={project_code}
        usedDtSyncIds={usedDtSyncIds}
      />
    </Grid>
  );
};

export default MasterPage;